import { Fragment } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { rememberChosenPeriod, resolveDefaultPeriodId } from '@/core/periodDefault';
import { OUTPUT_PROFILES, outputProfileLabel, type OutputProfile } from '@/core/outputProfile';
import { selectedClient } from '@/core/selectedClient';
import { SOLE_TRADER_CLIENT_TYPE, useClientType } from '@/core/useClientType';
import { approvalService } from '@/core/services/approval';
import { phase2Service } from '@/core/services/phase2';
import { practiceService } from '@/core/services/practice';
import type { ValidationCentreIssue, ValidationCentreScreen } from '@/core/models/phase2.models';
import type { PeriodRow } from '@/core/models/practice.models';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { validateForm } from '@/features/manage/formRules';
import { Button } from '@/kit/Button';
import { InputField } from '@/kit/FormField';
import { Icon } from '@/kit/Icon';
import { RequiredMark } from '@/kit/RequiredMark';
import { Select } from '@/kit/Select';
import { NO_PERIOD_PLACEHOLDER } from '@/core/format/periodLabel';
import { NoPeriodNote, fixUrl, periodTo, useAlive, useClientInit, useLive } from './common';
import './TaxreturnValidationcentre.scss';

interface Model {
  periods: PeriodRow[];
  /** APT355: the client's periods have been read (so "no accounting period" is only said once it is known). */
  periodsLoaded: boolean;
  periodId: string;
  category: string;
  /** ALL covers the whole period; otherwise the eligibility issues of one kind of accounts. */
  outputProfile: OutputProfile | 'ALL';
  categories: string[];
  screen: ValidationCentreScreen | null;
  issues: ValidationCentreIssue[];
  acceptIssue: ValidationCentreIssue | null;
  acceptReason: string;
  /** A reason was submitted and the answer has not come back (stops a second click). */
  accepting: boolean;
  /** The Accept click has been tried with the reason empty (draws the red outline). */
  acceptTried: boolean;
}

/**
 * The validation centre (AP-010): accounting, disclosure, eligibility, working paper, iXBRL-readiness and filing
 * issues in one list, with severity, fix links and the override audit. A blocking issue must be fixed; a warning can
 * be accepted with a reason by an authorised reviewer. (Angular TaxreturnValidationcentreComponent.)
 */
export function TaxreturnValidationcentre() {
  const alive = useAlive();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // APT352: Finalise's "Open Validation Centre" names the kind of accounts being finalised (`?profile=MEMBERS`); anything else covers the period.
  const requestedProfile = OUTPUT_PROFILES.find((p) => p.code === (searchParams.get('profile') ?? '').toUpperCase())?.code;
  const [m, set, ref] = useLive<Model>({
    periods: [],
    periodsLoaded: false,
    periodId: '',
    category: 'All',
    outputProfile: requestedProfile ?? 'ALL',
    categories: ['All'],
    screen: null,
    issues: [],
    acceptIssue: null,
    acceptReason: '',
    accepting: false,
    acceptTried: false,
  });

  const bind = (screen: ValidationCentreScreen) => {
    set({ screen, issues: screen.issues ?? [], categories: ['All', ...(screen.categories ?? [])] });
  };

  const load = async (): Promise<ValidationCentreScreen | null> => {
    const s = ref.current;
    if (!s.periodId) {
      return null;
    }
    try {
      const screen =
        s.outputProfile === 'ALL'
          ? await phase2Service.getValidationCentre(s.periodId, s.category)
          : await phase2Service.getValidationCentre(s.periodId, s.category, s.outputProfile);
      if (alive.current) bind(screen);
      return screen;
    } catch (error) {
      if (alive.current) notifyError(error);
      return null;
    }
  };

  useClientInit(async (client) => {
    try {
      const result = await practiceService.getClientPeriods(client.id);
      if (!alive.current) return;
      const periods = result.periods ?? [];
      if (periods.length > 0) {
        // The period the user last chose, else the latest with data, else the latest that has ended (see pickDefaultPeriodId).
        set({ periods, periodsLoaded: true, periodId: (await resolveDefaultPeriodId(client.id, periods)) ?? periods[0].id });
        await load();
      } else {
        set({ periods, periodsLoaded: true });
      }
    } catch (error) {
      if (alive.current) notifyError(error);
    }
  });

  /**
   * APT352: stores a validation run on the current figures. Finalising needs one that is up to date, and this screen only reads the
   * last one, so until now the only way to make it was to open Submit.
   */
  const runValidations = async () => {
    const periodId = ref.current.periodId;
    if (!periodId) {
      return;
    }
    try {
      const run = await approvalService.runValidations(periodId);
      if (!alive.current) return;
      const screen = await load();
      if (!alive.current) return;
      // The list below also holds the filing and eligibility checks, which the stored run (what Finalise reads) does not: say so, so
      // the message never reads "no blocking issues" beside blocking rows.
      const listed = screen?.blockingCount ?? 0;
      if (run.blockingCount > 0) {
        notifySuccess(`Validations run: ${run.blockingCount} blocking issue(s) to fix before the accounts can be finalised.`);
      } else if (listed > 0) {
        notifySuccess(
          `Validations run: the accounts can be finalised, but ${listed} blocking issue(s) listed below must be fixed before they can be filed.`,
        );
      } else {
        notifySuccess('Validations run: no blocking issues. The accounts can be finalised.');
      }
    } catch (error) {
      if (alive.current) notifyError(error);
    }
  };

  /** Deep-links to the screen that resolves the issue. */
  const fix = (issue: ValidationCentreIssue) => {
    if (!issue.fixRoute) {
      return;
    }
    navigate(fixUrl(issue.fixRoute));
  };

  const openAccept = (issue: ValidationCentreIssue) => {
    set({ acceptIssue: issue, acceptReason: issue.override?.reason ?? '', acceptTried: false });
  };

  const cancelAccept = () => {
    set({ acceptIssue: null, acceptReason: '', acceptTried: false });
  };

  const save = async (issue: ValidationCentreIssue, reason: string | null) => {
    if (ref.current.accepting) {
      return;
    }
    set({ accepting: true });
    try {
      const screen = await phase2Service.overrideIssue({
        accountingPeriodId: ref.current.periodId,
        ruleCode: issue.ruleCode,
        objectId: issue.objectId,
        scope: issue.category,
        severity: issue.severityId,
        reason,
      });
      if (!alive.current) return;
      bind(screen);
      cancelAccept();
      notifySuccess(reason === null ? 'The acceptance has been removed.' : 'The issue has been accepted and audited.');
    } catch (error) {
      if (alive.current) notifyError(error);
    } finally {
      if (alive.current) set({ accepting: false });
    }
  };

  const accept = () => {
    const s = ref.current;
    if (!s.acceptIssue || s.accepting) {
      return;
    }
    if (!s.acceptReason.trim()) {
      set({ acceptTried: true });
      validateForm(false);
      return;
    }
    void save(s.acceptIssue, s.acceptReason.trim());
  };

  const clearAccept = (issue: ValidationCentreIssue) => {
    void save(issue, null);
  };

  const { screen, acceptIssue } = m;
  // A sole trader has only the Full accounts, so there is nothing to scope the validations to.
  const isSoleTrader = useClientType(selectedClient()?.id) === SOLE_TRADER_CLIENT_TYPE;

  return (
    <SideMenuPage menu="task">
      <div className="content-body validation-centre">
        <div className="content-header mb-0">
          <div className="content-header-right mb-1 vc-filters">
            <div className="vc-filters-left">
              <Select
                className="w-150"
                openOnFocus
                panelClass="myPanelClass"
                items={m.categories}
                clearable={false}
                searchable={false}
                value={m.category}
                onChange={(value) => {
                  set({ category: value as string });
                  void load();
                }}
              />
              <Select
                className="w-250"
                openOnFocus
                panelClass="myPanelClass"
                placeholder={NO_PERIOD_PLACEHOLDER}
                items={m.periods}
                bindLabel="fromDate"
                bindValue="id"
                clearable={false}
                searchable={false}
                value={m.periodId || null}
                onChange={(value) => {
                  set({ periodId: value as string });
                  rememberChosenPeriod(value as string);
                  void load();
                }}
                renderLabel={periodTo}
                renderOption={periodTo}
              />
            </div>
            <div className="vc-filters-right">
              <Button
                className="action-button primary-button border-radius-6"
                disabled={!m.periodId}
                onClick={() => void runValidations()}
              >
                <Icon name="rule" />
                <span>Run validations</span>
              </Button>
              {!isSoleTrader && (
                <Select<{ code: OutputProfile | 'ALL'; label: string }>
                  className="w-200"
                  openOnFocus
                  panelClass="myPanelClass"
                  items={[{ code: 'ALL', label: 'All accounts' }, ...OUTPUT_PROFILES]}
                  bindLabel="label"
                  bindValue="code"
                  clearable={false}
                  searchable={false}
                  value={m.outputProfile}
                  onChange={(value) => {
                    set({ outputProfile: (value as OutputProfile | 'ALL' | null) ?? 'ALL' });
                    void load();
                  }}
                />
              )}
            </div>
          </div>
        </div>
        <NoPeriodNote show={m.periodsLoaded && m.periods.length === 0} />

        {screen && (
          <>
            <div className="text-gray font-size-14 mb-1">
              {/* item 139: clarifies that validations evaluate global settings & policies against this period's accounts */}
              Validations for <strong>{screen.periodLabel}</strong>: Evaluates client settings and policies against this
              period&rsquo;s accounts and trial balance.
            </div>
            <p>
              {screen.blockingCount} blocking, {screen.warningCount} warnings, {screen.informationCount} for
              information, {screen.overriddenCount} accepted.
              {screen.isRunStale && <span> The stored validation run is out of date; run the validations again.</span>}
              {!screen.canFinalise && (
                <span> These accounts cannot be finalised until the blocking issues are fixed.</span>
              )}
            </p>
          </>
        )}

        <div className="mat-table-wrapper">
          {/* APT252: a real table head and fixed column widths, so the header lines up with its cells (see the .scss). */}
          <table className="wp-100 validation-centre-table">
            <colgroup>
              <col className="vc-col-severity" />
              <col className="vc-col-category" />
              <col className="vc-col-issue" />
              <col className="vc-col-rule" />
              <col className="vc-col-actions" />
            </colgroup>
            <thead>
              <tr>
                <th scope="col">Severity</th>
                <th scope="col">Category</th>
                <th scope="col">Issue</th>
                <th scope="col">Rule</th>
                <th scope="col">Action</th>
              </tr>
            </thead>
            <tbody>
              {m.issues.map((issue, index) => (
                <Fragment key={`${issue.ruleCode}|${issue.objectId}|${index}`}>
                  <tr>
                    <td>
                      <span className={`vc-severity vc-severity--${issue.severity.toLowerCase()}`}>
                        {issue.severity}
                      </span>
                    </td>
                    <td>
                      {issue.category}
                      {issue.outputProfile && (
                        <span className="text-gray"> ({outputProfileLabel(issue.outputProfile)})</span>
                      )}
                    </td>
                    <td>
                      <span className="vc-message">{issue.message}</span>
                      {issue.detail && <small className="vc-sub">{issue.detail}</small>}
                      {issue.override && (
                        <small className="vc-sub">
                          Accepted: {issue.override.reason}
                          {issue.override.isStale && <span> (the figures have changed since)</span>}
                        </small>
                      )}
                    </td>
                    <td>
                      <span className="vc-message">{issue.ruleCode}</span>
                      {issue.sourceReference && <small className="vc-sub">{issue.sourceReference}</small>}
                    </td>
                    <td>
                      <div className="d-flex align-items-center grid-actions vc-actions">
                        {issue.fixRoute && (
                          <Button title="Fix" onClick={() => fix(issue)}>
                            <Icon name="build" outlined />
                          </Button>
                        )}
                        {issue.canOverride && !issue.override && !screen?.isLocked && (
                          <Button title="Accept" onClick={() => openAccept(issue)}>
                            <Icon name="check_circle_outline" outlined />
                          </Button>
                        )}
                        {issue.override && !screen?.isLocked && (
                          <Button title="Remove acceptance" onClick={() => clearAccept(issue)}>
                            <Icon name="undo" outlined />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {acceptIssue &&
                    acceptIssue.ruleCode === issue.ruleCode &&
                    acceptIssue.objectId === issue.objectId && (
                      <tr>
                        <td colSpan={5}>
                          <div className="d-flex align-items-center">
                            <InputField
                              fieldClassName="mr-1 wp-100"
                              placeholder="Reason for accepting this issue"
                              maxLength={500}
                              invalid={m.acceptTried && !m.acceptReason.trim()}
                              value={m.acceptReason}
                              onChange={(event) => set({ acceptReason: event.target.value })}
                            />
                            <span className="mr-1">
                              <RequiredMark />
                            </span>
                            <Button className="action-button primary-button mr-1" busy={m.accepting} onClick={accept}>
                              Accept
                            </Button>
                            <Button className="action-button secondary-button" onClick={cancelAccept}>
                              Cancel
                            </Button>
                          </div>
                        </td>
                      </tr>
                    )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </SideMenuPage>
  );
}
